<template>
  <div
    v-if="state.enabled"
    class="viewport-debug"
    :class="{ 'viewport-debug--collapsed': state.collapsed }"
  >
    <div class="viewport-debug__head">
      <strong @click="state.collapsed = !state.collapsed">
        viewport debug
        <template v-if="state.collapsed">
          · {{ buildVersion }} · {{ state.innerHeight }} / {{ state.documentHeightVar || '—' }}
          <template v-if="state.logCount"> · logs:{{ state.logCount }}</template>
        </template>
      </strong>
      <button
        type="button"
        @click="state.collapsed = !state.collapsed"
      >
        {{ state.collapsed ? '▲' : '▼' }}
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        @click="refresh"
      >
        refresh
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        @click="copyPanel"
      >
        {{ state.copyStatus || 'copy' }}
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        @click="clearLogs"
      >
        clear logs
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        :class="{ 'is-on': state.layersOn }"
        @click="toggleLayers"
      >
        layers
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        :class="{ 'is-on': state.inspectArmed }"
        @click="armInspect"
      >
        {{ state.inspectArmed ? 'tap…' : 'inspect' }}
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        @click="disable"
      >
        off
      </button>
    </div>
    <pre v-if="!state.collapsed">{{ text }}</pre>
  </div>
  <div
    v-if="state.enabled && state.layersOn"
    class="viewport-debug-layers"
  >
    <div
      v-for="(box, i) in state.layerBoxes"
      :key="`${i}-${box.sel}`"
      class="viewport-debug-layers__box"
      :style="box.style"
    >
      {{ box.sel }}
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive } from 'vue'
import { pwaUtils } from '@/utils/pwaUtils.js'
import { getDocumentHeightDebugInfo } from '@/utils/syncDocumentHeight.js'
import { getTelegramViewportDebugInfo } from '@/utils/syncTelegramViewportHeight.js'
import store from '@/store/store.js'
import { router } from '@/router/router.js'
import { appType, appTypes } from '@/appType/appType.js'
import { settings } from '@/configs/settings.js'

/** localStorage: переживает reload; обычные юзеры флаг не ставят */
const STORAGE_KEY = 'debugViewport'
/** Bump to drop sticky flag / dumps after temporary forced-debug sessions */
const STORAGE_EPOCH = '3'
const EPOCH_KEY = 'debugViewportEpoch'
const FORCE_ENABLED = false
const SECRET_TAPS = 7
const SECRET_TAP_WINDOW_MS = 10_000
const SECRET_CORNER_SIZE = 72
const MAX_LOG_LINES = 40
const POLL_MS = 1000
const LAYER_SELECTORS = [
  { sel: 'html', color: '#ff3b30' },
  { sel: 'body', color: '#ff9500' },
  { sel: '#app', color: '#ffcc00' },
  { sel: '.baseLayout', color: '#34c759' },
  { sel: '.home_layout', color: '#00c7be' },
  { sel: '.pageWrapper', color: '#007aff' },
  { sel: '.pageWrapperOverflow', color: '#5856d6' },
  { sel: '.head_new', color: '#af52de' },
  { sel: '.navigation', color: '#ff2d55' },
  { sel: '.content', color: '#64d2ff' },
  { sel: '.premium-features', color: '#ffd60a' },
  { sel: '.training-day-card', color: '#ff9f0a' },
]
const LAYER_COLORS = LAYER_SELECTORS.map(l => l.color)

const buildVersion = settings.env.VUE_APP_VERSION || '—'

/** @type {{ t: string, level: string, msg: string }[]} */
const logBuffer = []

const state = reactive({
  enabled: false,
  collapsed: false,
  copyStatus: '',
  updatedAt: '',
  innerHeight: 0,
  innerWidth: 0,
  clientHeight: 0,
  clientWidth: 0,
  offsetHeight: 0,
  screenHeight: 0,
  screenAvailHeight: 0,
  vvHeight: 0,
  vvWidth: 0,
  vvOffsetTop: 0,
  vvOffsetLeft: 0,
  vvScale: 0,
  documentHeightVar: '',
  documentOffsetBottom: '',
  bodyClientHeight: 0,
  standalone: false,
  displayMode: '',
  visibility: '',
  navHeight: 0,
  navTop: 0,
  gapBelowNav: 0,
  hasNavigationEl: false,
  routeName: '',
  routePath: '',
  layoutMeta: '',
  isRestoring: false,
  hideNavBar: false,
  appChildren: 0,
  logCount: 0,
  logsText: '',
  syncPrimed: false,
  syncCollapsed: false,
  syncAppliedHeight: 0,
  syncInstalledPwa: false,
  tgViewportHeight: 0,
  tgViewportStableHeight: 0,
  tgVisibleHeight: '',
  tgIsExpanded: '—',
  tgIsStable: '—',
  tgAppliedHeight: 0,
  tgCssHeight: '',
  tgCssStable: '',
  tgSdkMounted: false,
  tgSdkCssBound: false,
  tgSdkError: '',
  layersOn: false,
  layerBoxes: [],
  inspectArmed: false,
  inspectDump: '',
})

const text = computed(() => {
  const lines = [
    `t: ${state.updatedAt}`,
    `version: ${buildVersion}`,
    `route: ${state.routeName || '—'} (${truncate(state.routePath || '—', 80)})`,
    `layout: ${state.layoutMeta || '—'}`,
    `isRestoring: ${state.isRestoring}`,
    `hideNavBar: ${state.hideNavBar}`,
    `#app.children: ${state.appChildren}`,
    `.navigation: ${state.hasNavigationEl}`,
    `standalone: ${state.standalone}`,
    `display-mode: ${state.displayMode}`,
    `visibility: ${state.visibility}`,
    `appType: ${appType}`,
    `inner: ${state.innerWidth}×${state.innerHeight}`,
    `client(html): ${state.clientWidth}×${state.clientHeight}`,
    `offset(html): ${state.offsetHeight}`,
    `body.clientH: ${state.bodyClientHeight}`,
    `screen: ${state.screenHeight} / avail ${state.screenAvailHeight}`,
    `vv: ${state.vvWidth}×${state.vvHeight}`,
    `vv.offsetTop: ${state.vvOffsetTop}`,
    `vv.scale: ${state.vvScale}`,
    `--document-height: ${state.documentHeightVar}`,
    `--document-offset-bottom: ${state.documentOffsetBottom}`,
    `nav.h: ${state.navHeight}`,
    `nav.top: ${state.navTop}`,
    `gap below nav: ${state.gapBelowNav}`,
  ]

  if (appType === appTypes.TELEGRAM) {
    lines.push(
      `--- telegram ---`,
      `tg.viewportHeight: ${state.tgViewportHeight || '—'}`,
      `tg.viewportStableHeight: ${state.tgViewportStableHeight || '—'}`,
      `tg.css.height: ${state.tgCssHeight || '—'}`,
      `tg.css.stable: ${state.tgCssStable || '—'}`,
      `tg.visibleH: ${state.tgVisibleHeight || '—'}`,
      `tg.isExpanded: ${state.tgIsExpanded}`,
      `tg.isStable: ${state.tgIsStable}`,
      `tg.appliedH: ${state.tgAppliedHeight || '—'}`,
      `tg.sdk.mounted: ${state.tgSdkMounted}`,
      `tg.sdk.cssBound: ${state.tgSdkCssBound}`,
      `tg.sdk.error: ${state.tgSdkError || '—'}`,
    )
  }

  lines.push(
    `--- sync ---`,
    `sync.installedPwa: ${state.syncInstalledPwa}`,
    `sync.primed: ${state.syncPrimed}`,
    `sync.collapsed: ${state.syncCollapsed}`,
    `sync.appliedHeight: ${state.syncAppliedHeight}`,
    `--- inspect ---`,
    state.inspectDump || 'кнопка inspect → тап по тёмному блоку (не по панели)',
    `--- layers ---`,
    state.layersOn
      ? state.layerBoxes.map(b => b.sel).join('\n') || LAYER_SELECTORS.map(l => l.sel).join('\n')
      : 'кнопка layers: рамки скроллеров / padding / кнопки у низа',
    `--- console (${state.logCount}) ---`,
    state.logsText || '(пусто)',
    `---`,
    `on: 7 тапов впр.низу за 10с / off: кнопка`,
  )

  return lines.join('\n')
})

function truncate(value, max) {
  const str = String(value)
  if (str.length <= max) {
    return str
  }
  return `${str.slice(0, max - 1)}…`
}

function pushLog(level, args) {
  const msg = args.map((a) => {
    if (typeof a === 'string') {
      return a
    }
    try {
      return JSON.stringify(a)
    } catch {
      return String(a)
    }
  }).join(' ')

  logBuffer.push({
    t: new Date().toLocaleTimeString('ru-RU'),
    level,
    msg: msg.slice(0, 500),
  })

  while (logBuffer.length > MAX_LOG_LINES) {
    logBuffer.shift()
  }

  syncLogsToState()
}

function syncLogsToState() {
  state.logCount = logBuffer.length
  state.logsText = logBuffer
    .map(l => `[${l.t}] ${l.level}: ${l.msg}`)
    .join('\n')
}

function clearLogs() {
  logBuffer.length = 0
  syncLogsToState()
}

function installConsoleCapture() {
  const levels = ['error', 'warn', 'log']
  const originals = {}

  levels.forEach((level) => {
    originals[level] = console[level].bind(console)
    console[level] = (...args) => {
      try {
        pushLog(level, args)
      } catch {
        // never break console
      }
      originals[level](...args)
    }
  })

  const onError = (event) => {
    const msg = event?.message || String(event)
    const file = event?.filename ? ` @ ${event.filename}:${event.lineno || 0}` : ''
    pushLog('error', [`window.onerror: ${msg}${file}`])
  }

  const onRejection = (event) => {
    pushLog('error', [`unhandledrejection: ${event?.reason ?? event}`])
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)

  return () => {
    levels.forEach((level) => {
      console[level] = originals[level]
    })
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}

function readEnabled() {
  try {
    const epoch = localStorage.getItem(EPOCH_KEY)
    if (epoch !== STORAGE_EPOCH) {
      localStorage.setItem(EPOCH_KEY, STORAGE_EPOCH)
      localStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem('debugViewportAutoDumps')
      return FORCE_ENABLED
    }
    if (FORCE_ENABLED) {
      return true
    }
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return FORCE_ENABLED
  }
}

function setEnabled(value) {
  state.enabled = value
  try {
    if (value) {
      localStorage.setItem(STORAGE_KEY, '1')
      refresh()
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // ignore
  }
}

function disable() {
  state.layersOn = false
  state.inspectArmed = false
  state.layerBoxes = []
  setEnabled(false)
}

function describeEl(el) {
  if (!el) {
    return '(null)'
  }
  if (el === document.documentElement) {
    return 'html'
  }
  if (el === document.body) {
    return 'body'
  }
  const id = el.id ? `#${el.id}` : ''
  const rawClass = typeof el.className === 'string' ? el.className.trim() : ''
  const cls = rawClass
    ? `.${rawClass.split(/\s+/).slice(0, 5).join('.')}`
    : ''
  return `${el.tagName.toLowerCase()}${id}${cls}`
}

function dumpNode(el) {
  const cs = getComputedStyle(el)
  const maxScroll = Math.max(0, Math.round(el.scrollHeight - el.clientHeight))
  return [
    describeEl(el),
    `  overflow: ${cs.overflow} / y:${cs.overflowY}`,
    `  bg: ${cs.backgroundColor}`,
    `  padB: ${cs.paddingBottom}  overscroll: ${cs.overscrollBehaviorY}`,
    `  scrollTop: ${Math.round(el.scrollTop)} / max ${maxScroll}`,
    `  size: ${Math.round(el.clientWidth)}×${Math.round(el.clientHeight)}`,
  ].join('\n')
}

function measureLayers() {
  if (!state.layersOn) {
    state.layerBoxes = []
    return
  }

  const seen = new Set()
  const found = []

  function add(el, note) {
    if (!el || seen.has(el) || el.closest?.('.viewport-debug') || el.closest?.('.viewport-debug-layers')) {
      return
    }
    seen.add(el)
    const rect = el.getBoundingClientRect()
    if (rect.width < 8 || rect.height < 8) {
      return
    }
    found.push({ el, note, rect })
  }

  LAYER_SELECTORS.forEach(({ sel }) => {
    document.querySelectorAll(sel).forEach((el, i) => {
      add(el, i ? `${sel}[${i}]` : sel)
    })
  })

  document.querySelectorAll('div, main, section, button').forEach((el) => {
    const cs = getComputedStyle(el)
    if (cs.overflowY === 'auto' || cs.overflowY === 'scroll') {
      add(el, `scroll y:${cs.overflowY} pb:${cs.paddingBottom}`)
    }
  })

  document.querySelectorAll('button').forEach((el) => {
    const rect = el.getBoundingClientRect()
    const nearBottom = rect.bottom > window.innerHeight - 180 && rect.top < window.innerHeight
    if (nearBottom && rect.height >= 28) {
      add(el, 'btn-near-bottom')
    }
  })

  state.layerBoxes = found.slice(0, 24).map((item, i) => {
    const color = LAYER_COLORS[i % LAYER_COLORS.length]
    const rect = item.rect
    return {
      sel: `${describeEl(item.el)} · ${item.note}`,
      style: {
        top: `${Math.round(rect.top)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        height: `${Math.round(rect.height)}px`,
        borderColor: color,
        color,
      },
    }
  })
}

function toggleLayers() {
  state.layersOn = !state.layersOn
  measureLayers()
}

function armInspect() {
  state.inspectArmed = !state.inspectArmed
  if (state.inspectArmed) {
    state.inspectDump = 'ожидаю тап по экрану…'
  }
}

function onInspectPointer(event) {
  if (!state.enabled || !state.inspectArmed) {
    return
  }
  if (event.target?.closest?.('.viewport-debug')) {
    return
  }

  let clientX
  let clientY
  if (event.changedTouches?.[0]) {
    clientX = event.changedTouches[0].clientX
    clientY = event.changedTouches[0].clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }

  const stack = document.elementsFromPoint(clientX, clientY)
    .filter(el => !el.closest?.('.viewport-debug') && !el.classList.contains('viewport-debug-layers__box'))
    .slice(0, 12)

  const lines = [
    `tap: ${Math.round(clientX)}, ${Math.round(clientY)}`,
    `--- path ---`,
    ...stack.map(dumpNode),
  ]
  state.inspectDump = lines.join('\n')
  state.inspectArmed = false
  state.collapsed = false
}

async function copyPanel() {
  try {
    await navigator.clipboard.writeText(text.value)
    state.copyStatus = 'copied'
  } catch {
    state.copyStatus = 'fail'
  }
  window.setTimeout(() => {
    state.copyStatus = ''
  }, 1500)
}

function getDisplayMode() {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone'
  }
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui'
  }
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen'
  }
  if (window.matchMedia('(display-mode: browser)').matches) {
    return 'browser'
  }
  return navigator.standalone ? 'navigator.standalone' : 'unknown'
}

function measureNav() {
  const nav = document.querySelector('.navigation')
  if (!nav) {
    state.hasNavigationEl = false
    state.navHeight = 0
    state.navTop = 0
    state.gapBelowNav = 0
    return
  }
  const rect = nav.getBoundingClientRect()
  state.hasNavigationEl = true
  state.navHeight = Math.round(rect.height)
  state.navTop = Math.round(rect.top)
  state.gapBelowNav = Math.round(window.innerHeight - rect.bottom)
}

function refresh() {
  const root = document.documentElement
  const styles = getComputedStyle(root)
  const vv = window.visualViewport
  const route = router.currentRoute.value

  state.updatedAt = new Date().toLocaleTimeString('ru-RU')
  state.innerHeight = window.innerHeight
  state.innerWidth = window.innerWidth
  state.clientHeight = root.clientHeight
  state.clientWidth = root.clientWidth
  state.offsetHeight = root.offsetHeight
  state.bodyClientHeight = document.body?.clientHeight ?? 0
  state.screenHeight = window.screen?.height ?? 0
  state.screenAvailHeight = window.screen?.availHeight ?? 0
  state.vvHeight = Math.round(vv?.height ?? 0)
  state.vvWidth = Math.round(vv?.width ?? 0)
  state.vvOffsetTop = Math.round(vv?.offsetTop ?? 0)
  state.vvOffsetLeft = Math.round(vv?.offsetLeft ?? 0)
  state.vvScale = Number((vv?.scale ?? 1).toFixed(3))
  state.documentHeightVar = styles.getPropertyValue('--document-height').trim()
  state.documentOffsetBottom = styles.getPropertyValue('--document-offset-bottom').trim() || '(empty)'
  state.standalone = pwaUtils.isStandalone()
  state.displayMode = getDisplayMode()
  state.visibility = document.visibilityState
  state.routeName = String(route?.name ?? '')
  state.routePath = String(route?.fullPath ?? '')
  state.layoutMeta = String(route?.meta?.layout ?? '')
  state.isRestoring = !!store.state.user.isRestoring
  state.hideNavBar = !!store.state.app.hideNavBar
  state.appChildren = document.getElementById('app')?.childElementCount ?? 0
  measureNav()
  measureLayers()
  syncLogsToState()

  if (appType === appTypes.TELEGRAM) {
    const tg = window.Telegram?.WebApp
    const tgSdk = getTelegramViewportDebugInfo()
    const sdk = window.telegramSDK
    let sdkHeight = 0
    let sdkStable = 0
    let sdkExpanded = ''
    try {
      sdkHeight = Math.round(Number(sdk?.viewportHeight?.() ?? 0))
      sdkStable = Math.round(Number(sdk?.viewportStableHeight?.() ?? 0))
      if (typeof sdk?.isViewportExpanded === 'function') {
        sdkExpanded = String(!!sdk.isViewportExpanded())
      }
    } catch {
      // ignore
    }

    state.tgViewportHeight = Math.round(tg?.viewportHeight ?? sdkHeight ?? tgSdk.height ?? 0)
    state.tgViewportStableHeight = Math.round(tg?.viewportStableHeight ?? sdkStable ?? tgSdk.stable ?? 0)
    state.tgIsExpanded = tg
      ? String(!!tg.isExpanded)
      : (sdkExpanded || (tgSdk.expanded == null ? '—' : String(tgSdk.expanded)))
    state.tgIsStable = tgSdk.isStable == null ? '—' : String(tgSdk.isStable)
    state.tgAppliedHeight = Math.round(tgSdk.applied || 0)
    state.tgCssHeight = styles.getPropertyValue('--tg-viewport-height').trim()
    state.tgCssStable = styles.getPropertyValue('--tg-viewport-stable-height').trim()
    state.tgVisibleHeight = styles.getPropertyValue('--tma-visible-height').trim()
    state.tgSdkMounted = !!tgSdk.mounted
    state.tgSdkCssBound = !!tgSdk.cssBound
    state.tgSdkError = tgSdk.error || ''
  }

  const sync = getDocumentHeightDebugInfo()
  state.syncPrimed = sync.primed
  state.syncCollapsed = sync.collapsed
  state.syncAppliedHeight = Math.round(sync.appliedHeight)
  state.syncInstalledPwa = !!sync.installedPwa
}

let stop = null
let secretTapTimes = []

function isSecretCornerTap(clientX, clientY) {
  const fromRight = window.innerWidth - clientX <= SECRET_CORNER_SIZE
  const fromBottom = window.innerHeight - clientY <= SECRET_CORNER_SIZE
  return fromRight && fromBottom
}

function onSecretPointer(event) {
  let clientX
  let clientY

  if (event.changedTouches?.[0]) {
    clientX = event.changedTouches[0].clientX
    clientY = event.changedTouches[0].clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }

  if (!isSecretCornerTap(clientX, clientY)) {
    return
  }

  const now = Date.now()
  secretTapTimes = secretTapTimes.filter(t => now - t <= SECRET_TAP_WINDOW_MS)
  secretTapTimes.push(now)

  if (secretTapTimes.length >= SECRET_TAPS) {
    secretTapTimes = []
    setEnabled(!state.enabled)
  }
}

onMounted(() => {
  const stopConsole = installConsoleCapture()
  state.enabled = readEnabled()

  if (state.enabled) {
    refresh()
  }

  const onResume = () => {
    if (!state.enabled) {
      return
    }
    refresh()
  }

  window.addEventListener('resize', refresh)
  window.visualViewport?.addEventListener('resize', refresh)
  window.addEventListener('orientationchange', onResume)
  window.addEventListener('pageshow', onResume)
  document.addEventListener('visibilitychange', onResume)

  const secretEvent = 'ontouchstart' in window ? 'touchend' : 'click'
  document.addEventListener(secretEvent, onSecretPointer, {passive: true})
  document.addEventListener('pointerdown', onInspectPointer, {capture: true, passive: true})
  window.addEventListener('scroll', measureLayers, {capture: true, passive: true})

  const intervalId = window.setInterval(() => {
    if (state.enabled) {
      refresh()
    }
  }, POLL_MS)

  stop = () => {
    stopConsole()
    window.removeEventListener('resize', refresh)
    window.visualViewport?.removeEventListener('resize', refresh)
    window.removeEventListener('orientationchange', onResume)
    window.removeEventListener('pageshow', onResume)
    document.removeEventListener('visibilitychange', onResume)
    document.removeEventListener(secretEvent, onSecretPointer)
    document.removeEventListener('pointerdown', onInspectPointer, {capture: true})
    window.removeEventListener('scroll', measureLayers, {capture: true})
    window.clearInterval(intervalId)
  }
})

onUnmounted(() => {
  stop?.()
})
</script>

<style scoped>
.viewport-debug {
  position: fixed;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 99999;
  max-height: 55vh;
  overflow: auto;
  padding: 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.82);
  color: #9ef01a;
  font: 11px/1.35 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  pointer-events: auto;
  -webkit-overflow-scrolling: touch;
}

.viewport-debug--collapsed {
  right: auto;
  max-height: none;
  overflow: visible;
}

.viewport-debug__head {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
  color: #fff;
}

.viewport-debug--collapsed .viewport-debug__head {
  margin-bottom: 0;
}

.viewport-debug__head strong {
  cursor: pointer;
  user-select: none;
}

.viewport-debug__head button {
  padding: 2px 8px;
  border-radius: 4px;
  background: #333;
  color: #fff;
  font-size: 11px;
}

.viewport-debug__head button.is-on {
  background: #0a84ff;
}

.viewport-debug pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.viewport-debug-layers {
  pointer-events: none;
}

.viewport-debug-layers__box {
  position: fixed;
  z-index: 99998;
  box-sizing: border-box;
  border: 2px solid;
  font: 10px/1.2 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  padding: 2px 4px;
  background: transparent;
  white-space: nowrap;
  overflow: hidden;
}
</style>
